import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  CompanyApi,
  CompanyResponseModel,
  DeviceIds,
  GeneralManagementApi,
  GetCompanyInstallationsCollectionResponse,
  InstallationCollection,
  InstllationsMigrationApi,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { UIService } from '@services';
import { hasMainDeviceRole } from '../installation-details';
import { InstallationsMigrationActions } from './installations-migration.actions';

@Injectable()
export class InstallationsMigrationEffects {
  listCompanyCustomerInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsMigrationActions.listCompanyCustomerInstallations),
      switchMap((action) => {
        const params = action.params;

        return this.generalManagementApi
          .getCompanyInstallationsCollection(params.oldCompanyNumber, params.limit, params.cursor)
          .pipe(
            takeUntil(
              this.actions$.pipe(ofType(InstallationsMigrationActions.Type.CLEAR_CACHED_DATA))
            ),
            map((response: GetCompanyInstallationsCollectionResponse) => {
              const installationsList = response.installations || [];
              const cursor = response?.cursor;

              return InstallationsMigrationActions.listCompanyCustomerInstallationsSuccess({
                installations: installationsList.map((installation) =>
                  this.setMainDeviceOfGateway(installation)
                ),
                cursor,
              });
            }),
            catchError(() => {
              this.toast.error({
                message: this.translation.instant(
                  '+INSTALLATIONS_MIGRATION.INSTALLATIONS.LOADING_ERROR'
                ),
              });

              return of(InstallationsMigrationActions.listCompanyCustomerInstallationsError());
            })
          );
      })
    );
  });

  migrateInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsMigrationActions.migrateInstallations),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.api.migrateInstallations(action.params).pipe(
          takeUntil(
            this.actions$.pipe(ofType(InstallationsMigrationActions.Type.CLEAR_CACHED_DATA))
          ),
          map(() => {
            this.uiService.unblockUI();

            return InstallationsMigrationActions.migrateInstallationsSuccess();
          }),
          catchError(() => {
            this.uiService.unblockUI();
            this.toast.error({
              message: this.translation.instant('+INSTALLATIONS_MIGRATION.MIGRATION_ERROR_TOAST'),
            });

            return of(InstallationsMigrationActions.migrateInstallationsError());
          })
        )
      )
    );
  });

  getCompanyNames$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsMigrationActions.getCustomerCompanyNames),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        forkJoin([
          this.fetchCustomerCompany(action.customerNumber),
          this.fetchCustomerCompany(action.newCustomerNumber),
        ]).pipe(
          map((response: [CompanyResponseModel, CompanyResponseModel]) => {
            this.uiService.unblockUI();

            return InstallationsMigrationActions.getCustomerCompanyNamesSuccess({
              customerCompanyName: response[0]?.name,
              newCustomerCompanyName: response[1]?.name,
            });
          }),
          catchError(() => {
            this.uiService.unblockUI();

            return of(InstallationsMigrationActions.getCustomerCompanyNamesError());
          })
        )
      )
    );
  });

  private fetchCustomerCompany(customerNumber: string): Observable<CompanyResponseModel> {
    return this.companyApi
      .getUserCompany(customerNumber)
      .pipe(catchError((error) => (error.statusCode === 404 ? of(null) : of(error))));
  }

  private setMainDeviceOfGateway(installation: InstallationCollection): InstallationCollection {
    return {
      ...installation,
      gateways: installation?.gateways?.map((gateway) => {
        return {
          ...gateway,
          devices: gateway.devices?.map((device, index) => {
            return {
              ...device,
              isMainDeviceOfGateway:
                hasMainDeviceRole(device) ||
                device.id === DeviceIds.Main ||
                (index === 0 && device.id !== DeviceIds.Gateway),
            };
          }),
        };
      }),
    };
  }

  constructor(
    private api: InstllationsMigrationApi,
    private companyApi: CompanyApi,
    private generalManagementApi: GeneralManagementApi,
    private toast: ViToastService,
    private uiService: UIService,
    private translation: TranslateService,
    private actions$: Actions
  ) {}
}
